import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from '@mui/material';
import { ExpandMore, Code } from '@mui/icons-material';
import { FaNodeJs } from "react-icons/fa";
import BaseTab from './BaseTab';

interface FrontendInstallationTabProps {
  isDark: boolean;
  colors: any;
  discordFont: any;
  glassStyle: any;
  lokiGradient: string;
  goldColor: string;
  isMobile: boolean;
}

const FrontendInstallationTab: React.FC<FrontendInstallationTabProps> = (props) => {
  return (
    <BaseTab
      {...props}
      title="Frontend Installation & Setup"
      emoji="📦"
    >
      <Typography variant="h6" sx={{ color: props.goldColor, mb: 3 }}>
        Quick Start Guide
      </Typography>
      
      <Paper sx={{ bgcolor: alpha('#051814', 0.8), p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ color: props.goldColor, mb: 2 }}>
          Prerequisites
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FaNodeJs size={30} color={props.goldColor} />
              <Typography variant="body2" color={props.colors.textPrimary}>
                Node.js ≥ 18.0.0
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Code sx={{ color: props.goldColor }} />
              <Typography variant="body2" color={props.colors.textPrimary}>
                npm ≥ 9.0.0 or yarn ≥ 1.22.0
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Code sx={{ color: props.goldColor }} />
              <Typography variant="body2" color={props.colors.textPrimary}>
                Git for version control
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ color: props.goldColor }} />}>
          <Typography fontWeight={700} color={props.colors.textPrimary}>
            Step 1: Clone Repository
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ bgcolor: '#1E1F22', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" fontFamily="monospace" color={props.goldColor}>
              git clone https://github.com/Kaiftokare19/WFHCafeDev-Frontend.git<br />
              cd WFHCafeDev-Frontend
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ color: props.goldColor }} />}>
          <Typography fontWeight={700} color={props.colors.textPrimary}>
            Step 2: Install Dependencies
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ bgcolor: '#1E1F22', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" fontFamily="monospace" color={props.goldColor}>
              npm install<br />
              # or<br />
              yarn install
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ color: props.goldColor }} />}>
          <Typography fontWeight={700} color={props.colors.textPrimary}>
            Step 3: Environment Configuration
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper sx={{ bgcolor: '#1E1F22', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" fontFamily="monospace" color={props.goldColor}>
              # Create environment file<br />
              cp .env.example .env.local<br /><br />
              
              # Configure environment variables<br />
              VITE_API_URL=http://localhost:3000<br />
              VITE_GOOGLE_MAPS_API_KEY=your_google_maps_api_key
            </Typography>
          </Paper>
        </AccordionDetails>
      </Accordion>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: props.goldColor }}>
          Available Scripts
        </Typography>
        <Grid container spacing={2}>
          {[
            { command: 'npm run dev', desc: 'Start development server' },
            { command: 'npm run build', desc: 'Production build' },
            { command: 'npm run lint', desc: 'Run ESLint' },
            { command: 'npm run preview', desc: 'Preview production build' },
            { command: 'npm run type-check', desc: 'TypeScript type checking' },
          ].map((script, idx) => (
            <Grid size={{ xs: 12, md: 6 }} key={idx}>
              <Paper sx={{ p: 2, borderLeft: `4px solid ${props.goldColor}` }}>
                <Typography variant="body2" fontFamily="monospace" color={props.goldColor}>
                  {script.command}
                </Typography>
                <Typography variant="caption" color={props.colors.textSecondary}>
                  {script.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </BaseTab>
  );
};

export default FrontendInstallationTab;